/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import firebase from 'firebase/app'
import 'firebase/analytics'
import 'firebase/auth'
import 'firebase/firestore'
import 'firebase/functions'
import 'firebase/storage'
import 'firebase/remote-config'

// Attach to global window
window.firebase = firebase

/**
 * This class represents which server environment we are using.
 */
export default class Environment {

    /** All environments */
    static environments = [

        /** Production on the GCP backend */
        {

            // Environment info
            id: 'production_gcp',
            isDefault: true,
            domains: ['spaces.vatom.com', 'localhost'],

            // Vatom info
            vatomClientID: '3H5qpyiQu9',
            servers: {
                id: 'https://id.vatom.com',
                events: 'https://events.api.vatominc.com',
                billing: 'https://billing.api.vatominc.com',
                studio: 'https://studio.api.vatominc.com',
                resourcesAdmin: 'https://resources.api.vatominc.com',
                resourcesCDN: 'https://resources.vatom.com',
            },

            // Firebase info
            firebaseConfig: {
                apiKey: "AIzaSyDaqQT25pnhacf1Mdl0lC4zT2ezMO14pi4",
                authDomain: "ydangle-high-fidelity-test-2.firebaseapp.com",
                databaseURL: "https://ydangle-high-fidelity-test-2.firebaseio.com",
                projectId: "ydangle-high-fidelity-test-2",
                storageBucket: "ydangle-high-fidelity-test-2.appspot.com",
                messagingSenderId: "190616353628",
                appId: "1:190616353628:web:81bdd0a9e8fe382d2a1f99"
            },

        },

        /** Staging (test) environment */
        {

            // Environment info
            id: 'test',
            domains: ['spaces.vatominc.cc'],

            // Vatom info
            vatomClientID: 'i9JbYBQHWS0LjHoe8ckhz',
            servers: {
                id: 'https://id.vatominc.cc',
                events: 'https://events.api.vatominc.cc',
                billing: 'https://billing.api.vatominc.cc',
                studio: 'https://studio.api.vatominc.cc',
                resourcesAdmin: 'https://resources.api.vatominc.cc',
                resourcesCDN: 'https://resources.vatominc.cc',
            },

            // Firebase info
            firebaseConfig: {
                apiKey: "AIzaSyDmV2eyp_v934pQVztWdn4OcU8oPjPwdQw",
                authDomain: "u-shared-395-fbase.firebaseapp.com",
                databaseURL: "https://u-shared-395-fbase-default-rtdb.firebaseio.com",
                projectId: "u-shared-395-fbase",
                storageBucket: "u-shared-395-fbase.appspot.com",
                messagingSenderId: "638189905199",
                appId: "1:638189905199:web:4016a700e53251f203edda"
            },

        },

        /** Development environment */
        {

            // Environment info
            id: 'development',
            domains: ['spaces.vatominc.cool'],

            // Vatom info
            vatomClientID: 'g8XPzf9r9s44oP7piuLR_',
            servers: {
                id: 'https://id.vatominc.cool',
                events: 'https://events.api.vatominc.cool',
                billing: 'https://billing.api.vatominc.cool',
                studio: 'https://studio.api.vatominc.cool',
                resourcesAdmin: 'https://resources.api.vatominc.cool',
                resourcesCDN: 'https://resources.vatominc.cool',
            },

            // Firebase info
            firebaseConfig: {
                apiKey: "AIzaSyD-t903xRru7QdgYZxWEy6T5WHWJJ6EmSw",
                authDomain: "devtest-546f3.firebaseapp.com",
                databaseURL: "https://devtest-546f3.firebaseio.com",
                projectId: "devtest-546f3",
                storageBucket: "devtest-546f3.appspot.com",
                messagingSenderId: "1080858465240",
                appId: "1:1080858465240:web:f7a8221df0ad43aff318ad"
            },

        },
    
    ]

    /** @type {Environment} Get current environment */
    static get current() {

        // Use cached if it exists
        if (Environment._current)
            return Environment._current

        // If we have a build-time environment override, use it
        if (process.env.VSPACES_OVERRIDE_ENVIRONMENT) {

            // Find matching data
            let data = Environment.environments.find(e => e.id == process.env.VSPACES_OVERRIDE_ENVIRONMENT)
            if (data) {
                console.debug(`[Environment] Using '${data.id}' since VSPACES_OVERRIDE_ENVIRONMENT has been set.`)
                return Environment._current = new Environment(data)
            } else {
                console.warn(`[Environment] The environment specified by VSPACES_OVERRIDE_ENVIRONMENT (${process.env.VSPACES_OVERRIDE_ENVIRONMENT}) was not found.`)
            }

        }

        // Find matching environment data
        let data = Environment.environments.find(e => e.domains.includes(window.location.hostname))
        if (data)
            return Environment._current = new Environment(data)

        // Unknown environment! Use the default one
        let defaultData = Environment.environments.find(e => e.isDefault)
        console.warn(`[Environment] Unknown app environment for '${window.location.hostname}', using the default '${defaultData.id}' environment`)
        return Environment._current = new Environment(defaultData)

    }

    /** Current environment data */
    data = {}

    /** Constructor */
    constructor(data) {
        this.data = data
    }

    /** Environment ID */
    get id() {
        return this.data.id
    }

    /** Setup the environment */
    setup() {

        // Setup individual systems
        console.debug(`[Environment] Using the '${this.id}' environment`)
        this.setupFirebase()

    }

    /** Setup Firebase */
    setupFirebase() {

        // Setup Firebase config
        firebase.initializeApp(this.data.firebaseConfig)

        // If using the emulator, set it
        if (process.env.VSPACES_USE_FIREBASE_EMULATOR) {

            // Setup emulator access
            console.warn(`[Environment] Using local Firebase emulator as VSPACES_USE_FIREBASE_EMULATOR has been set.`)
            firebase.auth().useEmulator('http://localhost:9099')
            firebase.firestore().useEmulator('localhost', 8080)
            firebase.functions().useEmulator('localhost', 5001)
            firebase.storage().useEmulator('localhost', 9199)

            // Prevent the emulator warning bar which firebase adds automatically from interfering with the UI
            let style = document.createElement('style')
            style.innerText = `
                .firebase-emulator-warning { opacity: 0.25; pointer-events: none; bottom: 60px !important; }
            `
            document.body.appendChild(style)

        }

    }

    /** Get Firebase storage public access URL */
    get firebaseStorageURL() {
        return 'https://storage.googleapis.com/' + (this.data.firebaseConfig.storageBucket || '<missing bucket>')
    }

}
